import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CustomerService, DatadogRumService } from '../../services';
import { UserService } from '../../services';
import { ICustomerPremiumTimeBand } from '../../interfaces/customer-premium-timeband';
import { Moment } from 'moment';
import { BBWTMessageService, OrganisationSettingService } from '../../services';
import { OrganisationSettingEnum } from '../../enums/organisation-settings';
import moment from 'moment';
import {
  DailyMovementLimitForTaskType,
  DailyMovementLimitResultForTaskType,
  DailyMovementLimitService,
} from '../../services/daily-movement-limit.service';
import format from 'date-fns/format';
import { TaskType } from 'src/app/enums/task.type';

enum LimitType {
  Global = 'Global',
  Customer = 'Customer',
}

@Component({
  selector: 'app-job-time-window',
  templateUrl: './job-time-window.component.html',
  styleUrls: ['./job-time-window.component.scss'],
})
export class JobTimeWindowComponent implements OnInit {
  timeError: boolean;
  msgs: { severity: string; summary: string; detail: string }[];

  private daysOverLimit: string[] = [];

  constructor(
    private orgSettingService: OrganisationSettingService,
    private customerService: CustomerService,
    public messageService: BBWTMessageService,
    private userService: UserService,
    private movementLimitService: DailyMovementLimitService,
    private datadogRumService: DatadogRumService,
  ) {}

  public minDate: Date;
  dateRange: Date[] = [];

  public movementLimits: DailyMovementLimitResultForTaskType[] = [];

  dateTimeFormat = 'ddd DD MMMM HH:mm';
  dateFormat = 'ddd DD MMMM';
  timeFormat = 'HH:mm';
  goodLeadTime = 36;
  maxLeadTimeCheck = 72;
  goodTimeWindow = 30;
  warningTimeWindow = 8;
  errorTimeWindow = 3;
  dangerTimeWindow = 4;
  defaultDangerTimeWindow = 4;

  // these are set whenever date or times are modified to reflect summary text/warnings
  summaryText: string;
  leadTimeIcon: string;
  leadTimeText: string;
  leadTimeInfo: string; // tooltip on the icon
  windowIcon: string;
  windowText: string;
  windowInfo: string; // tooltip on the icon

  userOrgId: number; // customerId
  premiumTimeBands: ICustomerPremiumTimeBand[];

  @Input()
  isLocationSet: boolean;

  @Input() bypassOrderLimitations: boolean;

  _isPickup: boolean;
  @Input('isPickup') set isPickup(value: boolean) {
    this._isPickup = value;

    this.desc = this._isPickup ? 'collected' : 'delivered';
    this.altDesc = this._isPickup ? 'collection' : 'delivery';
  }

  get isPickup(): boolean {
    return this._isPickup;
  }

  desc: string;
  altDesc: string;

  @Input()
  organisationId: string;

  @Input()
  disabled: boolean;

  @Input()
  locationForm: UntypedFormGroup;

  private _hoursInterval: number = 1;
  private _minutesInterval: number = 5;
  @Input()
  set minutesInterval(value: number) {
    this._minutesInterval = value || 5;
    this.initTimes();
  }

  @Output() dateTimeWindowChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() taskType: TaskType;

  @Input('earliestDateTime') set earliestDateTime(value: Date) {
    this.dateRange[0] = value;
    this.setInitialTimes();
  }

  get earliestDateTime(): Date {
    return this.dateRange[0];
  }

  @Input('latestDateTime') set latestDateTime(value: Date) {
    this.dateRange[1] = value;

    this.setInitialTimes();
    this.locationForm?.updateValueAndValidity();
  }

  get latestDateTime(): Date {
    return this.dateRange[1];
  }

  private _earliestTime: any;
  get earliestTime(): any {
    return this._earliestTime;
  }

  set earliestTime(value: any) {
    this._earliestTime = value;
    this.setTimes();
  }

  private _latestTime: any;
  get latestTime(): any {
    return this._latestTime;
  }

  set latestTime(value: any) {
    this._latestTime = value;
    this.setTimes();
  }

  public times: any[];

  private lastLimitCheckStartDate: string | null = null;
  private lastLimitCheckEndDate: string | null = null;

  async ngOnInit() {
    this.minDate = moment().toDate();

    if (this.isPickup) {
      await this.getMovementLimits();
    }

    let userOrg = await this.userService.getCurrentUserOrg();
    this.userOrgId = userOrg.id;

    let settingId = this._isPickup
      ? OrganisationSettingEnum.MinCollectionWindow
      : OrganisationSettingEnum.MinDeliveryWindow;
    let orgSettings = await this.orgSettingService.search({
      component: 'customer-details',
      organisationId: this.userOrgId,
      includeOrganisation: false,
    });
    let parsedSettings = this.orgSettingService.parseSettings(orgSettings);

    this.errorTimeWindow =
      parsedSettings[settingId] !== undefined
        ? parsedSettings[settingId].intValue
        : this.defaultDangerTimeWindow;

    // load customer premium rates to check against time windows
    this.premiumTimeBands = await this.customerService.getPremiumTimeBands(this.userOrgId);

    this.initTimes();
    this.setInitialTimes();
  }

  async getMovementLimits() {
    try {
      this.movementLimits = await this.movementLimitService.getDailyMovementLimitsForTaskType(
        this.taskType,
      );
      this.setDaysOverLimit();
    } catch (e) {}
  }

  formatDuration(duration): string {
    let parts = [];

    // return nothing when the duration is falsy or not correctly parsed (P0D)
    if (!duration || duration.toISOString() === 'P0D') return;

    if (duration.years() >= 1) {
      const years = Math.floor(duration.years());
      parts.push(years + ' ' + (years > 1 ? 'years' : 'year'));
    }

    if (duration.months() >= 1) {
      const months = Math.floor(duration.months());
      parts.push(months + ' ' + (months > 1 ? 'months' : 'month'));
    }

    if (duration.days() >= 1) {
      const days = Math.floor(duration.days());
      parts.push(days + ' ' + (days > 1 ? 'days' : 'day'));
    }

    if (duration.hours() >= 1) {
      const hours = Math.floor(duration.hours());
      parts.push(hours + ' ' + (hours > 1 ? 'hours' : 'hour'));
    }

    if (duration.minutes() >= 1) {
      const minutes = Math.floor(duration.minutes());
      parts.push(minutes + ' ' + (minutes > 1 ? 'minutes' : 'minute'));
    }

    return parts.join(', ');
  }

  getDateDesc(index: number): string {
    if (this.dateRange[index]) {
      return moment(this.dateRange[index]).format(this.dateFormat);
    } else {
      if (index == 1) {
        return moment(this.dateRange[0]).format(this.dateFormat);
      }
    }
  }

  private initTimes() {
    this.times = ((): any[] => {
      let res = [];
      for (let hour = 0; hour < 24; hour += this._hoursInterval) {
        for (let minute = 0; minute < 60; minute += this._minutesInterval) {
          res.push({
            label: `${leftPad(hour, 2)}:${leftPad(minute, 2)}`,
            value: { hours: hour, minutes: minute },
          });
        }
      }
      return res;
    })();

    function leftPad(number: number, targetLength: number) {
      let output = number + '';
      while (output.length < targetLength) {
        output = '0' + output;
      }
      return output;
    }
  }

  private setInitialTimes() {
    if (!this.dateRange[0] || !this.dateRange[1]) return;

    // if the time components are not the same as current, set them
    if (
      !this._earliestTime ||
      this._earliestTime.hours != this.earliestDateTime.getHours() ||
      this._earliestTime.minutes != this.earliestDateTime.getMinutes()
    ) {
      this._earliestTime = {
        hours: moment(this.dateRange[0]).hours(),
        minutes: moment(this.dateRange[0]).minutes(),
      };
    }
    if (
      !this._latestTime ||
      this._latestTime.hours != this.latestDateTime.getHours() ||
      this._latestTime.minutes != this.latestDateTime.getMinutes()
    ) {
      let datetimeEarly = moment(this.dateRange[0]);
      let datetimeLate = moment(this.dateRange[1]);

      if (datetimeLate.isBefore(datetimeEarly, 'minutes')) {
        datetimeLate = datetimeLate
          .hours(datetimeEarly.hours())
          .minutes(datetimeEarly.minutes())
          .add(this._minutesInterval, 'minutes');
      }
      this._latestTime = { hours: datetimeLate.hours(), minutes: datetimeLate.minutes() };
    }

    if (this.isPickup) {
      this.locationForm?.controls.pickUpWindowUnderLimit.setValue(this.isWindowValidForLimits());
      this.locationForm?.updateValueAndValidity();
    }

    // this method is called after the route time validation has returned the values from the
    // parent component - so need to update summary info in case the values have
    // been changed.
    this.updateSummaryInfo(moment(this.dateRange[0]), moment(this.dateRange[1]));
  }

  public setTimes(raiseChangeEvent: boolean = true) {
    if (!this.dateRange[1]) return;

    // set date objects to reflect time selection
    let datetimeEarly = moment(this.dateRange[0])
      .hours(this._earliestTime.hours)
      .minutes(this._earliestTime.minutes);
    let datetimeLate = moment(this.dateRange[1])
      .hours(this._latestTime.hours)
      .minutes(this._latestTime.minutes);

    // update summary info for current selection
    this.updateSummaryInfo(datetimeEarly, datetimeLate);

    // Update Summary may have changed some values
    datetimeEarly = moment(this.dateRange[0])
      .hours(this._earliestTime.hours)
      .minutes(this._earliestTime.minutes);
    datetimeLate = moment(this.dateRange[1])
      .hours(this._latestTime.hours)
      .minutes(this._latestTime.minutes);

    if (this.isPickup) {
      this.locationForm?.controls.pickUpWindowUnderLimit.setValue(this.isWindowValidForLimits());
    }

    if (raiseChangeEvent) {
      this.dateTimeWindowChange.emit([datetimeEarly.toDate(), datetimeLate.toDate()]);
      this.locationForm?.updateValueAndValidity();
    }
  }

  public isWindowValidForLimits(): boolean {
    if (this.bypassOrderLimitations) {
      return true;
    }

    if (!this.limitCheckRangeChanged()) {
      return this.locationForm.controls.pickUpWindowUnderLimit.value;
    }

    this.lastLimitCheckStartDate = this.dateRange[0].toLocaleDateString();
    this.lastLimitCheckEndDate = this.dateRange[1].toLocaleDateString();

    // If there are no days over limit, the window is valid.
    if (this.daysOverLimit.length === 0) {
      return true;
    }

    const dateEnd = new Date(this.dateRange[1]);

    for (
      let currentDate = new Date(this.dateRange[0]);
      currentDate <= dateEnd;
      currentDate.setDate(currentDate.getDate() + 1)
    ) {
      const formattedDate = format(currentDate, 'yyyy-MM-dd');

      if (!this.daysOverLimit.includes(formattedDate)) {
        return true;
      }
    }

    this.datadogRumService.addAction('order-limits-warning', {
      selectedPickupWindow: [this.dateRange[0].toLocaleDateString(), dateEnd.toLocaleDateString()],
      daysLimited: this.movementLimits.filter((x) => this.daysOverLimit.includes(x.date)),
      taskType: this.taskType,
      customerId: this.organisationId,
    });

    return false;
  }

  private limitCheckRangeChanged(): boolean {
    return (
      this.dateRange[0].toLocaleDateString() != this.lastLimitCheckStartDate ||
      this.dateRange[1].toLocaleDateString() != this.lastLimitCheckEndDate
    );
  }

  public setDaysOverLimit() {
    if (this.movementLimits.length == 0) {
      return;
    }

    const output = [];

    for (let limit of this.movementLimits) {
      if (this.checkLimit(limit.global) && this.checkLimit(limit.customer)) {
        continue;
      }
      output.push(limit.date);
    }

    this.daysOverLimit = output;
  }

  private checkLimit(dailyLimit: DailyMovementLimitForTaskType): boolean {
    if (dailyLimit == null) {
      return true;
    }

    return dailyLimit.limit == null || dailyLimit.total < dailyLimit.limit;
  }

  private updateSummaryInfo(dateTimeEarly: Moment, dateTimeLate: Moment) {
    // update lead time data
    const duration = moment.duration(dateTimeLate.diff(moment()));

    this.leadTimeText = `<b>Lead Time:</b> ${this.formatDuration(duration)}`;
    this.leadTimeInfo = 'Long lead time';

    let leadTime = dateTimeLate.diff(moment(), 'hour', true);
    if (leadTime < this.maxLeadTimeCheck) {
      if (leadTime >= this.goodLeadTime) {
        this.leadTimeIcon = this.getAcceptableIcon();
        this.leadTimeInfo = `Lead time is ok; extend it further if you can`;
      } else {
        this.leadTimeIcon = this.getWarningIcon();
        this.leadTimeInfo = `Booking is short notice - book further out if you can`;
      }
    } else {
      this.leadTimeIcon = this.getOKIcon();
    }

    // update time-window data
    const windowDuration = moment.duration(dateTimeLate.diff(dateTimeEarly));
    this.windowText = `<b>Time Window:</b> ${this.formatDuration(windowDuration)}`;

    let timeWindow = dateTimeLate.diff(dateTimeEarly, 'hours', true);
    let daysWindow = dateTimeLate.diff(dateTimeEarly, 'days', true);

    let premium = this.premiumTimeBands
      ? this.premiumTimeBands[this.premiumTimeBands.length - 1]
      : null;
    let inLastBand = true;
    if (premium && (premium.minTime >= timeWindow || premium.maxTime < timeWindow)) {
      inLastBand = false;
    }

    this.windowIcon = this.getOKIcon();
    this.timeError = false;

    if (timeWindow >= this.goodTimeWindow && inLastBand) {
      this.windowInfo =
        'Wide time window - your flexibility will minimise the cost of this movement';
    } else {
      if (timeWindow > this.warningTimeWindow) {
        this.windowIcon = this.getAcceptableIcon();
        this.windowInfo = 'Time window is ok; widen it if you can';
      } else if (timeWindow > this.dangerTimeWindow) {
        this.windowIcon = this.getWarningIcon();
        this.windowInfo = 'Time window is narrow; enter more flexible times if you can';
      } else if (timeWindow < this.errorTimeWindow) {
        this.messageService.ShowError(
          'Time Window',
          'Time window was too narrow and has been increased (Minimum time window is ' +
            this.errorTimeWindow +
            ' hours)',
        );

        this.windowIcon = this.getErrorIcon();
        this.windowInfo =
          '<b>Time window was too narrow and has been increased (Minimum time window is ' +
          this.errorTimeWindow +
          ' hours)</b>';
        this.timeError = true;

        let newLatest = dateTimeEarly.clone().add(this.errorTimeWindow, 'hours');
        this._latestTime = { hours: newLatest.hours(), minutes: newLatest.minutes() };
        this.dateRange[1] = newLatest.toDate();
      } else {
        this.windowIcon = this.getDangerIcon();
        this.windowInfo = 'Time window is narrow; enter more flexible times if you can';
      }
    }
  }

  getSummaryText(dateTimeEarly, dateTimeLatest) {
    // update summary text
    let dateDesc = 'Your vehicle will be ' + this.desc + ' ';
    let dateTimeEarlyMoment = moment(dateTimeEarly);
    let dateTimeLatestMoment = moment(dateTimeLatest);

    if (
      dateTimeEarlyMoment.format(this.dateFormat) != dateTimeLatestMoment.format(this.dateFormat)
    ) {
      dateDesc += `between <b>${dateTimeEarlyMoment.format(
        this.dateTimeFormat,
      )}</b> and <b>${dateTimeLatestMoment.format(this.dateTimeFormat)}</b>`;
    } else {
      dateDesc += `on <b>${dateTimeEarlyMoment.format(
        this.dateFormat,
      )}</b> between <b>${dateTimeEarlyMoment.format(this.timeFormat)}</b> 
        and <b>${dateTimeLatestMoment.format(this.timeFormat)}</b>`;
    }

    return dateDesc;
  }

  getDateClass(date) {
    let start = this.dateRange[0];
    let end = this.dateRange[1] || this.dateRange[0];

    let momentDate = moment(new Date(date.year, date.month, date.day));

    const classes = [];

    if (this.isPickup && this.daysOverLimit.includes(momentDate.format('yyyy-MM-DD'))) {
      classes.push('text-red-300');
    }

    if (
      momentDate.isSame(this.dateRange[0], 'day') &&
      momentDate.isSame(this.dateRange[1], 'day')
    ) {
      classes.push('same-date');
      return classes;
    } else if (momentDate.isSame(start, 'day')) {
      classes.push('start-date');
      return classes;
    } else if (momentDate.isSame(end, 'day')) {
      classes.push('end-date');
      return classes;
    }

    if (momentDate.isAfter(start, 'day') && momentDate.isBefore(end, 'day')) {
      classes.push('mid-date');
    }

    return classes;
  }

  getAcceptableIcon() {
    return 'fa fa-warning yellow-icon';
  }

  getWarningIcon() {
    return 'fa fa-warning red-amber-icon';
  }

  getErrorIcon() {
    return 'fa fa-warning red-icon';
  }

  getDangerIcon() {
    return 'fa fa-warning red-icon';
  }

  getOKIcon() {
    return 'fa fa-check-circle green-icon';
  }
}
